/**
 * PublishConfirm.jsx
 * The last thing between a draft and the public internet.
 *
 * It names the exact Page and Instagram handle the post will reach, with the
 * image beside them. That naming is the guard against the mistake this app
 * could most easily make: putting one brand's post on another brand's account.
 */

import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Check, Warning, Link, Clock } from './Icons.jsx';
import ImageLightbox from './ImageLightbox.jsx';
import { formatLocalInput, formatLondon } from '../utils/time.js';

/**
 * @param {'publish'|'schedule'} mode - publishing goes out now; scheduling goes
 *   out unattended later, so both name the destinations before committing.
 */
export default function PublishConfirm({
  postId,
  brandName,
  imageId,
  isVideo = false,
  mode = 'publish',
  whenLocal = null,
  onPublished,
  onCancel,
}) {
  const [destinations, setDestinations] = useState(null);
  const [error, setError] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState(null);
  const [viewing, setViewing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.posts
      .destinations(postId)
      .then((d) => !cancelled && setDestinations(d))
      .catch((e) => !cancelled && setError(e));
    return () => {
      cancelled = true;
    };
  }, [postId]);

  async function commit() {
    setPublishing(true);
    setError(null);
    try {
      if (mode === 'schedule') {
        const res = await api.posts.schedule(postId, whenLocal);
        setResults([
          {
            platform: 'scheduled',
            status: 'scheduled',
            when: res.post.scheduled_at_utc,
          },
        ]);
        onPublished?.(res);
        return;
      }

      const res = await api.posts.publish(postId);
      setResults(res.results);
      if (res.ok) onPublished?.(res);
    } catch (e) {
      setError(e);
    } finally {
      setPublishing(false);
    }
  }

  const notReady = destinations?.destinations.filter((d) => !d.ready) ?? [];

  if (viewing && imageId) {
    return (
      <ImageLightbox
        src={api.media.rawUrl(imageId)}
        isVideo={isVideo}
        caption="Last look before this goes out"
        onClose={() => setViewing(false)}
      />
    );
  }

  return (
    <div className="modal-backdrop" onClick={publishing ? undefined : onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>
          {results
            ? mode === 'schedule'
              ? 'Scheduled'
              : 'Publish results'
            : mode === 'schedule'
              ? 'Schedule this post?'
              : 'Publish this post?'}
        </h2>

        {!results && (
          <p className="modal__sub">
            {mode === 'schedule' ? (
              <>
                This will publish on its own at{' '}
                <strong>{formatLocalInput(whenLocal)}</strong>, with no further
                approval, to the accounts below.
              </>
            ) : (
              'This goes live immediately on the accounts below. It cannot be unpublished from here.'
            )}
          </p>
        )}

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        {/* ---------- Before publishing ---------- */}
        {!results && destinations && (
          <>
            <div className="confirm-body">
              {imageId &&
                (isVideo ? (
                  <video
                    className="confirm-thumb zoomable"
                    src={api.media.rawUrl(imageId)}
                    muted
                    playsInline
                    preload="metadata"
                    title="Click to watch it before it goes out"
                    onClick={() => setViewing(true)}
                  />
                ) : (
                  <img
                    className="confirm-thumb zoomable"
                    src={api.media.rawUrl(imageId)}
                    alt=""
                    title="Click to check it full size"
                    onClick={() => setViewing(true)}
                  />
                ))}

              <div className="rows" style={{ flex: 1 }}>
                {destinations.destinations.map((d) => (
                  <div className="row" key={d.platform}>
                    <span className={`tile tile--sm ${d.ready ? 'tile--ok' : 'tile--danger'}`}>
                      {d.ready ? <Check size={17} /> : <Warning size={17} />}
                    </span>
                    <div>
                      <div className="row__label">
                        {d.label} — {d.account ?? 'not connected'}
                      </div>
                      <div className="row__sub">
                        {brandName} · {d.captionLength} characters
                        {d.hasImage ? ' · with image' : ' · text only'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {notReady.length > 0 && (
              <div className="notice notice--error">
                <Warning />
                <span>
                  {notReady.map((d) => d.label).join(' and ')}{' '}
                  {notReady.length === 1 ? 'is' : 'are'} not connected for {brandName}. Connect on
                  the Brands screen first.
                </span>
              </div>
            )}
          </>
        )}

        {/* ---------- After publishing ---------- */}
        {results && mode === 'schedule' && (
          <div className="row">
            <span className="tile tile--sm tile--ok">
              <Clock size={17} />
            </span>
            <div>
              <div className="row__label">Goes out {formatLondon(results[0].when)}</div>
              <div className="row__sub">
                You can move or cancel it on the Calendar until then.
              </div>
            </div>
          </div>
        )}

        {results && mode === 'publish' && (
          <div className="rows">
            {results.map((r) => (
              <div className="row" key={r.platform}>
                <span
                  className={`tile tile--sm ${
                    r.status === 'failed' ? 'tile--danger' : r.status === 'processing' ? 'tile--warn' : 'tile--ok'
                  }`}
                >
                  {r.status === 'failed' ? <Warning size={17} /> : <Check size={17} />}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="row__label">
                    {r.platform === 'facebook' ? 'Facebook' : 'Instagram'} — {r.status}
                  </div>
                  <div className="row__sub">
                    {r.error ??
                      (r.status === 'processing'
                        ? 'Instagram is transcoding the video. It publishes automatically within a few minutes.'
                        : 'Published successfully')}
                  </div>
                </div>
                {r.url && (
                  <div className="row__end">
                    <a className="btn btn--sm" href={r.url} target="_blank" rel="noreferrer">
                      <Link size={15} /> View
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="btn-row modal__actions">
          {results ? (
            <button className="btn btn--primary" onClick={onCancel}>
              Done
            </button>
          ) : (
            <>
              <button
                className="btn btn--primary"
                onClick={commit}
                disabled={publishing || !destinations || notReady.length > 0}
              >
                {publishing
                  ? mode === 'schedule'
                    ? 'Scheduling…'
                    : 'Publishing…'
                  : mode === 'schedule'
                    ? 'Yes, schedule it'
                    : 'Yes, publish now'}
              </button>
              <button className="btn" onClick={onCancel} disabled={publishing}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
